import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../../../../components/AsyncStates';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { getBracket } from '@/api/TournamentManagement/tournamentApi';
import { MatchStatus, TournamentStatus, TournamentFormat, resolveEnum } from '@/models/tournament';
import type { BracketView, BracketRound, BracketMatchView, TournamentView, MatchView } from '@/models/tournament';
import GenerateBracketModal from './GenerateBracketModal';
import MatchDetailModal from './MatchDetailModal';
import { getMatch } from '@/api/TournamentManagement/matchApi';
import { parseApiWallClock } from '@/utils/apiDate';

interface BracketTabProps {
  tournamentId: number;
  tournament?: TournamentView;
}

/** Translates backend round name keys to i18n labels. */
function getRoundLabel(roundName: string, t: (key: string) => string): string {
  switch (roundName) {
    case 'Final': return t('Final');
    case 'Semi-final': return t('Semi-final');
    case 'Quarter-final': return t('Quarter-final');
    case 'Round of 16': return t('Round of 16');
    default: return roundName.startsWith('Round ')
      ? `${t('Round')} ${roundName.replace('Round ', '')}`
      : roundName;
  }
}

// ---------------------------------------------------------------------------
// Match Card
// ---------------------------------------------------------------------------

interface MatchCardProps {
  match: BracketMatchView;
  t: (key: string, opts?: Record<string, unknown>) => string;
  locale: string;
  onMatchClick?: (matchId: number) => void;
}

const statusColors: Record<number, string> = {
  [MatchStatus.Scheduled]: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  [MatchStatus.InProgress]: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  [MatchStatus.Completed]: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  [MatchStatus.Closed]: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
  [MatchStatus.Cancelled]: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

const statusLabels: Record<number, string> = {
  [MatchStatus.Scheduled]: 'Scheduled',
  [MatchStatus.InProgress]: 'In Progress',
  [MatchStatus.Completed]: 'Completed',
  [MatchStatus.Closed]: 'Closed',
  [MatchStatus.UnderRevision]: 'Under Revision',
  [MatchStatus.Cancelled]: 'Cancelled',
  6: 'Rescheduled',
};

const MatchCard: React.FC<MatchCardProps> = ({ match, t, locale, onMatchClick }) => {
  const status = resolveEnum(MatchStatus, match.status);
  const hasScore = match.team1Score != null && match.team2Score != null;
  const isTeam1Winner = match.winner === match.team1Name;
  const isTeam2Winner = match.winner === match.team2Name;

  const scheduledDate = parseApiWallClock(match.scheduledDateTime);
  const dateStr = scheduledDate
    ? scheduledDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    : '—';
  const timeStr = scheduledDate
    ? scheduledDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div
      className={`bg-card border border-border rounded-lg shadow-sm w-56 flex-shrink-0 overflow-hidden ${onMatchClick ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 hover:shadow-md transition-all' : ''}`}
      data-testid={`bracket-match-${match.matchId}`}
      onClick={() => onMatchClick?.(match.matchId)}
      role={onMatchClick ? 'button' : undefined}
      tabIndex={onMatchClick ? 0 : undefined}
      onKeyDown={onMatchClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onMatchClick(match.matchId); } } : undefined}
    >
      {/* Status bar */}
      <div className={`px-3 py-1.5 text-xs font-medium flex items-center justify-between ${status != null ? (statusColors[status] ?? 'bg-muted text-muted-foreground') : 'bg-muted text-muted-foreground'}`}>
        <span>{t(status != null ? (statusLabels[status] ?? 'Unknown') : 'Unknown')}</span>
        <span className="opacity-75">{dateStr}</span>
      </div>

      {/* Team rows */}
      <div className="divide-y divide-border">
        {/* Team 1 */}
        <div
          className={`flex items-center justify-between px-3 py-2.5 transition-colors ${
            isTeam1Winner ? 'bg-green-50 dark:bg-green-900/15' : ''
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {isTeam1Winner && (
              <Icon name="Trophy" size={12} className="text-green-600 dark:text-green-400 flex-shrink-0" />
            )}
            <span
              className={`text-sm truncate ${
                isTeam1Winner ? 'font-semibold text-green-700 dark:text-green-300' : 'text-foreground'
              }`}
              title={match.team1Name}
            >
              {match.team1Name}
            </span>
          </div>
          <span
            className={`text-sm font-mono tabular-nums ms-2 flex-shrink-0 ${
              isTeam1Winner ? 'font-bold text-green-700 dark:text-green-300' : 'text-muted-foreground'
            }`}
          >
            {hasScore ? match.team1Score : '-'}
          </span>
        </div>

        {/* Team 2 */}
        <div
          className={`flex items-center justify-between px-3 py-2.5 transition-colors ${
            isTeam2Winner ? 'bg-green-50 dark:bg-green-900/15' : ''
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {isTeam2Winner && (
              <Icon name="Trophy" size={12} className="text-green-600 dark:text-green-400 flex-shrink-0" />
            )}
            <span
              className={`text-sm truncate ${
                isTeam2Winner ? 'font-semibold text-green-700 dark:text-green-300' : 'text-foreground'
              }`}
              title={match.team2Name}
            >
              {match.team2Name}
            </span>
          </div>
          <span
            className={`text-sm font-mono tabular-nums ms-2 flex-shrink-0 ${
              isTeam2Winner ? 'font-bold text-green-700 dark:text-green-300' : 'text-muted-foreground'
            }`}
          >
            {hasScore ? match.team2Score : '-'}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 bg-muted/30 border-t border-border">
        <span className="text-xs text-muted-foreground">{timeStr}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Connector SVG between rounds
// ---------------------------------------------------------------------------

interface ConnectorProps {
  fromCount: number;
  toCount: number;
  matchCardHeight: number;
  gap: number;
}

const BracketConnector: React.FC<ConnectorProps> = ({ fromCount, toCount, matchCardHeight, gap }) => {
  if (fromCount === 0 || toCount === 0) return null;

  const connectorWidth = 32;
  const fromTotalHeight = fromCount * matchCardHeight + (fromCount - 1) * gap;
  const toTotalHeight = toCount * matchCardHeight + (toCount - 1) * gap;
  const svgHeight = Math.max(fromTotalHeight, toTotalHeight);

  const fromOffsetY = (svgHeight - fromTotalHeight) / 2;
  const toOffsetY = (svgHeight - toTotalHeight) / 2;

  const paths: string[] = [];

  for (let i = 0; i < toCount; i++) {
    // Each "to" match is fed by two consecutive "from" matches
    const fromIdx1 = i * 2;
    const fromIdx2 = i * 2 + 1;

    const toY = toOffsetY + i * (matchCardHeight + gap) + matchCardHeight / 2;

    if (fromIdx1 < fromCount) {
      const fromY1 = fromOffsetY + fromIdx1 * (matchCardHeight + gap) + matchCardHeight / 2;
      const midX = connectorWidth / 2;
      paths.push(`M 0 ${fromY1} C ${midX} ${fromY1} ${midX} ${toY} ${connectorWidth} ${toY}`);
    }
    if (fromIdx2 < fromCount) {
      const fromY2 = fromOffsetY + fromIdx2 * (matchCardHeight + gap) + matchCardHeight / 2;
      const midX = connectorWidth / 2;
      paths.push(`M 0 ${fromY2} C ${midX} ${fromY2} ${midX} ${toY} ${connectorWidth} ${toY}`);
    }
  }

  return (
    <svg
      width={connectorWidth}
      height={svgHeight}
      className="flex-shrink-0 hidden md:block"
      aria-hidden="true"
    >
      {paths.map((d, idx) => (
        <path
          key={idx}
          d={d}
          fill="none"
          stroke="currentColor"
          className="text-border"
          strokeWidth={1.5}
        />
      ))}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Main Bracket Tab
// ---------------------------------------------------------------------------

const BracketTab: React.FC<BracketTabProps> = ({ tournamentId, tournament }) => {
  const { t, i18n } = useTranslation('tournament');
  const [bracket, setBracket] = useState<BracketView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [detailMatch, setDetailMatch] = useState<MatchView | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const canGenerate = tournament
    && resolveEnum(TournamentFormat, tournament.format) === TournamentFormat.Knockout
    && (resolveEnum(TournamentStatus, tournament.status) === TournamentStatus.Upcoming
      || resolveEnum(TournamentStatus, tournament.status) === TournamentStatus.Active);

  const handleMatchClick = useCallback(async (matchId: number) => {
    try {
      const result = await getMatch(matchId);
      if (result.errors.length === 0 && result.payload) {
        setDetailMatch(result.payload);
        setShowDetailModal(true);
      }
    } catch {
      // silently fail — user can retry by clicking again
    }
  }, []);

  const fetchBracket = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBracket(tournamentId);
      if (result.errors.length > 0) {
        setError(result.errors[0]?.description || t('Failed to load bracket'));
      } else {
        setBracket(result.payload ?? { rounds: [] });
      }
    } catch {
      setError(t('Failed to load bracket'));
    } finally {
      setLoading(false);
    }
  }, [tournamentId, t]);

  useEffect(() => {
    fetchBracket();
  }, [fetchBracket]);

  // -------------------------------------------------------------------------
  // States
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="py-12">
        <LoadingState title={t('Loading bracket...')} size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start">
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 me-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
              {t('Error loading bracket')}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchBracket} className="mt-3">
              {t('Try Again')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!bracket || bracket.rounds.length === 0) {
    return (
      <>
        <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
          <Icon name="GitBranch" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('No bracket data yet')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('Bracket will appear once knockout matches with rounds are created.')}
          </p>
          {canGenerate && (
            <Button
              onClick={() => setShowGenerateModal(true)}
              data-testid="generate-bracket-btn"
            >
              <Icon name="Wand2" size={16} className="me-2" />
              {t('generateBracket')}
            </Button>
          )}
        </div>
        {canGenerate && (
          <GenerateBracketModal
            isOpen={showGenerateModal}
            onClose={() => setShowGenerateModal(false)}
            tournamentId={tournamentId}
            onGenerated={() => fetchBracket()}
          />
        )}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Desktop bracket: horizontal columns with connectors
  // -------------------------------------------------------------------------

  const MATCH_CARD_HEIGHT = 130; // approximate rendered height of a match card
  const MATCH_GAP = 16;

  return (
    <div data-testid="bracket-view">
      {/* Regenerate button */}
      {canGenerate && (
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowGenerateModal(true)}
            data-testid="regenerate-bracket-btn"
          >
            <Icon name="RefreshCw" size={14} className="me-1" />
            {t('generateBracket')}
          </Button>
        </div>
      )}

      {/* ---- Desktop: horizontal layout ---- */}
      <div
        ref={containerRef}
        className="hidden md:flex items-start gap-0 overflow-x-auto pb-4"
        data-testid="bracket-desktop"
      >
        {bracket.rounds.map((round, roundIdx) => {
          const nextRound: BracketRound | undefined = bracket.rounds[roundIdx + 1];

          return (
            <React.Fragment key={round.roundNumber}>
              {/* Round column */}
              <div className="flex flex-col items-center flex-shrink-0">
                {/* Round header */}
                <div className="mb-4 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold whitespace-nowrap">
                  {getRoundLabel(round.roundName, t)}
                </div>

                {/* Match cards */}
                <div
                  className="flex flex-col justify-center"
                  style={{ gap: `${MATCH_GAP}px` }}
                >
                  {round.matches.map((match) => (
                    <MatchCard
                      key={match.matchId}
                      match={match}
                      t={t}
                      locale={i18n.language}
                      onMatchClick={handleMatchClick}
                    />
                  ))}
                </div>
              </div>

              {/* Connector between this round and next */}
              {nextRound && (
                <div className="flex items-center flex-shrink-0 pt-10">
                  <BracketConnector
                    fromCount={round.matches.length}
                    toCount={nextRound.matches.length}
                    matchCardHeight={MATCH_CARD_HEIGHT}
                    gap={MATCH_GAP}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ---- Mobile: vertical stacked layout ---- */}
      <div className="md:hidden space-y-6" data-testid="bracket-mobile">
        {bracket.rounds.map((round) => (
          <div key={round.roundNumber}>
            {/* Round header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-border" />
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold whitespace-nowrap">
                {getRoundLabel(round.roundName, t)}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Match cards */}
            <div className="space-y-3">
              {round.matches.map((match) => (
                <MatchCard
                  key={match.matchId}
                  match={match}
                  t={t}
                  locale={i18n.language}
                  onMatchClick={handleMatchClick}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Generate/Regenerate Modal */}
      {canGenerate && (
        <GenerateBracketModal
          isOpen={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          tournamentId={tournamentId}
          onGenerated={() => fetchBracket()}
        />
      )}

      {/* Match Detail Modal */}
      <MatchDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setDetailMatch(null); }}
        match={detailMatch}
      />
    </div>
  );
};

export default BracketTab;
